import { useCallback } from "react";

import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { highlightActions, useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { Picker, renderActions, selectMainObject, selectMainObjects } from "features/render";
import { discardPendingOptimisticPaint, getPendingOptimisticPaint } from "features/render/hooks/sharedOptimisticPaint";
import { abortSharedSelection } from "features/render/hooks/sharedSelectionAbort";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

/** What entering the object-to-object tool does beyond setting the picker, as one undoable step. */
export type ObjectMeasureEntry = {
    apply: () => void;
    undo: () => void;
};

/**
 * What entering the object-to-object tool ({@link Picker.ObjectMeasurement}) does to the selection,
 * for whoever switches pickers to fold into its own undo step.
 *
 * The tool highlights its picks in the selection colour, so a selection left standing would read as
 * objects already picked. Cleared as part of entering, so undoing the entry brings it back. While a
 * click's optimistic paint is on screen, what is highlighted is that temporary fragment, so undo goes
 * back to the selection from before it - as a background click does.
 *
 * @returns `prepare(fromPicker, toPicker)`, to call before switching: the entry, or `undefined` when
 * the switch is not into the tool or there is no selection to clear.
 */
export function useObjectMeasureEntry() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const highlighted = useLazyHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();

    return useCallback(
        (fromPicker: Picker, toPicker: Picker): ObjectMeasureEntry | undefined => {
            if (toPicker !== Picker.ObjectMeasurement || fromPicker === Picker.ObjectMeasurement) {
                return;
            }

            const pendingPaint = getPendingOptimisticPaint();
            const previousSelectionIds = pendingPaint?.previousHighlightIds ?? highlighted.current.idArr;
            if (previousSelectionIds.length === 0 && highlighted.current.idArr.length === 0) {
                return;
            }

            const state = store.getState();
            const previousSelection = snapshotUndoIds(previousSelectionIds);
            const previousMainObject = pendingPaint ? pendingPaint.previousMainObject : selectMainObject(state);
            const previousMainObjects = pendingPaint ? pendingPaint.previousMainObjects : selectMainObjects(state);

            return {
                apply: () => {
                    // A selection still resolving would otherwise paint itself, or the one it
                    // replaced, back over the clear.
                    abortSharedSelection();
                    discardPendingOptimisticPaint();
                    dispatch(renderActions.setMainObject(undefined));
                    dispatch(renderActions.clearMainObjects());
                    dispatchHighlighted(highlightActions.setIds([]));
                },
                undo: () => {
                    dispatchHighlighted(highlightActions.setIds(restoreUndoIds(previousSelection)));
                    if (previousMainObject !== undefined) {
                        dispatch(renderActions.setMainObject(previousMainObject));
                    }
                    dispatch(renderActions.setMainObjects(previousMainObjects));
                },
            };
        },
        [dispatch, store, highlighted, dispatchHighlighted],
    );
}
